import React, { FC, ReactElement, useEffect } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { useFetchMe } from 'services/auth'
import { Dashboard } from 'features/Dashboard'
import { NotFound } from 'features/NotFound'
import { Login } from 'features/Login'
import { Loading } from 'components/Loading'
import { Project } from 'features/Project'
import { Projects } from 'features/Projects'
import { Layout } from 'components/Layout'
import { RequestPassword } from 'features/ForgotPassword/RequestPassword'
import { RecoverPassword } from 'features/ForgotPassword/RecoverPassword'
import { Account } from 'features/Account'
import { Users } from 'features/Organization/Users'
import { UserRoles, User } from 'models/user'
import { EditUser } from 'features/Organization/EditUser'
import { Organization } from 'features/Organization'
import { Datasets } from 'features/Datasets'
import { StorageProvider } from 'providers/storage'
import { Signup } from 'features/Signup'
import { CompleteInvitation } from 'features/CompleteInvitation'
import { PrivacyPolicy } from 'features/PrivacyPolicy'
import { TermsAndConditions } from 'features/TermsAndConditions'
import { Billing } from 'features/Billing'
import { useUserContext } from 'context/user-context'
import { ProjectReport } from 'features/ProjectReport'
import { Provider } from 'react-redux'
import store from 'store'
import 'App.css'

export const App: FC = () => {
  return (
    <Provider store={store}>
      <Routes>
        <PrivateRoute
          path="/dashboard"
          element={
            <Layout>
              <Dashboard />
            </Layout>
          }
        />
        <PrivateRoute path="/explore" element={<Project />} />
        <PrivateRoute path="/projects/:projectId" element={<Project />} />
        <PrivateRoute path="/projects/:projectId/report" element={<ProjectReport />} />
        <PrivateRoute path="/projects/:projectId/compare/:areaId" element={<Project />} />
        <PrivateRoute
          path="/projects"
          element={
            <Layout>
              <Projects />
            </Layout>
          }
        />

        <PrivateRoute
          path="/account/*"
          element={
            <Layout>
              <Account />
            </Layout>
          }
        />

        <PrivateRoute
          path="/organization/*"
          allowedRoles={[UserRoles.MANAGER, UserRoles.OWNER]}
          element={
            <Layout>
              <Organization />
            </Layout>
          }
        />

        <PrivateRoute
          path="/organization/users"
          allowedRoles={[UserRoles.MANAGER, UserRoles.OWNER]}
          element={
            <Layout>
              <Users />
            </Layout>
          }
        />

        <PrivateRoute
          path="/organization/users/:userId"
          allowedRoles={[UserRoles.MANAGER, UserRoles.OWNER]}
          element={
            <Layout>
              <EditUser />
            </Layout>
          }
        />

        <PrivateRoute
          path="/billing/*"
          allowedRoles={[UserRoles.OWNER]}
          element={
            <Layout>
              <Billing />
            </Layout>
          }
        />

        <PrivateRoute
          path="/datasets/*"
          allowedRoles={[UserRoles.MANAGER, UserRoles.OWNER]}
          element={
            <Layout>
              <Datasets />
            </Layout>
          }
        />

        <PublicRoute path="/signup" element={<Signup />} />
        <PublicRoute path="/invitations/:invitationId" element={<CompleteInvitation />} />
        <PublicRoute path="/login" element={<Login />} />
        <PublicRoute path="/" element={<Login />} />
        <PublicRoute path="/forgot-password" element={<RequestPassword />} />
        <PublicRoute path="/forgot-password/:uuid" element={<RecoverPassword />} />

        <Route
          path="/privacy-policy"
          element={
            <Layout>
              <PrivacyPolicy />
            </Layout>
          }
        />
        <Route
          path="/terms-and-conditions"
          element={
            <Layout>
              <TermsAndConditions />
            </Layout>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Provider>
  )
}

type PrivateRouteProps = {
  path: string
  element: ReactElement
  allowedRoles?: UserRoles[]
}

const PrivateRoute: FC<PrivateRouteProps> = ({ element, allowedRoles, ...rest }) => {
  const navigate = useNavigate()
  const [currentUser, setUser] = useUserContext()

  useFetchMe({
    onSuccess: (user: User) => setUser(user),
  })

  if (allowedRoles && currentUser) {
    const hasAccess = allowedRoles.some((role) => currentUser.isRole(role))

    if (!hasAccess) {
      navigate('/')
    }
  }

  return <Route {...rest} element={currentUser ? element : <Loading />} />
}

type PublicRouteProps = {
  path: string
  element: ReactElement
}

const PublicRoute: FC<PublicRouteProps> = (props) => {
  const navigate = useNavigate()

  useEffect(() => {
    if (StorageProvider.has('token')) {
      navigate('/')
    }
  }, [navigate])

  return <Route {...props} />
}
